/**
 * Conversion tracking utility for Meta and Reddit via CAPI
 * Sends conversion events to the capi-worker for server-side tracking
 *
 * Consent model: one banner on quickpoint.me (the marketing site) writes
 * qp_consent and qp_attr cookies on .quickpoint.me — the same registrable
 * domain as this app, same data controller. Nothing here stores or sends
 * ad-tracking data unless qp_consent=granted. See
 * quickpoint_website/js/attribution.js for the capture side.
 */

import { track } from './metrics';
import { fireGoogleSignupConversion } from './googleAds';
import { emitRybbitEvent } from './rybbit';

const CAPI_ENDPOINT = import.meta.env.PROD
  ? 'https://capi-worker.quickpointme.workers.dev'
  : 'http://localhost:8787';

interface ConversionUserData {
  fbclid?: string;
  rdt_cid?: string;
  /** plain text — the capi-worker SHA-256 hashes it before forwarding */
  email?: string;
  /** Reddit Pixel's _rdt_uuid first-party cookie (set on .quickpoint.me) */
  uuid?: string;
  /** marketing-site visitor id (qp_attr.v) — hashed by the capi-worker */
  external_id?: string;
  screen_width?: number;
  screen_height?: number;
}

interface ConversionEventData {
  event_name: string;
  user_data: ConversionUserData;
  custom_data?: Record<string, unknown>;
  event_id?: string;
}

interface Attribution {
  v?: string; // anonymous visitor id minted on the marketing site
  rdt_cid?: string;
  fbclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  lp?: string;
  ts?: number;
}

const getCookie = (name: string): string | null => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};

/** Ad-measurement consent, granted on the marketing site's banner. */
export const hasAdConsent = (): boolean => getCookie('qp_consent') === 'granted';

const readAttrCookie = (): Attribution => {
  try {
    return JSON.parse(getCookie('qp_attr') ?? 'null') ?? {};
  } catch {
    return {};
  }
};

/**
 * Attribution comes solely from the shared qp_attr cookie, written by the
 * marketing site's landing pages — ads never point at this app, so click
 * ids are never on this app's own URL.
 */
const getAttribution = (): Attribution => readAttrCookie();

/**
 * Merge campaign UTMs from the shared qp_attr cookie into a URL query string
 * for Rybbit. If the live URL already carries utm_* params they win (the marketing
 * site decorates author links with them); otherwise we backfill from the cookie
 * so cookie-only attribution still reaches Rybbit's pageview. Returns a string
 * beginning with "?" (or empty when there's nothing to send).
 */
export const withAttributionUtms = (search: string): string => {
  const params = new URLSearchParams(search);
  const hasUtm = [...params.keys()].some((k) => k.startsWith("utm_"));
  if (!hasUtm) {
    const attr = readAttrCookie();
    if (attr.utm_source) params.set("utm_source", attr.utm_source);
    if (attr.utm_medium) params.set("utm_medium", attr.utm_medium);
    if (attr.utm_campaign) params.set("utm_campaign", attr.utm_campaign);
    if (attr.utm_content) params.set("utm_content", attr.utm_content);
    if (attr.utm_term) params.set("utm_term", attr.utm_term);
  }
  const s = params.toString();
  return s ? `?${s}` : "";
};

/**
 * Match keys beyond the click id, so Reddit/Meta can join this event to the
 * same person's ad click even after the click id has gone stale. The
 * _rdt_uuid cookie is written by the Reddit Pixel on the marketing site
 * (scoped to .quickpoint.me, so readable here in production).
 */
const getMatchKeys = (attr: Attribution): ConversionUserData => ({
  fbclid: attr.fbclid,
  rdt_cid: attr.rdt_cid,
  external_id: attr.v,
  uuid: getCookie('_rdt_uuid') ?? undefined,
  screen_width: window.screen?.width,
  screen_height: window.screen?.height,
});

/**
 * Send a conversion event to the CAPI worker.
 * Routes to the appropriate endpoint based on click ID present.
 * Callers must have checked consent.
 */
const sendConversionEvent = async (
  eventData: ConversionEventData,
  platformNames: { facebook: string; reddit: string },
): Promise<void> => {
  const { fbclid, rdt_cid } = eventData.user_data;

  if (fbclid) {
    try {
      const fbPayload = { ...eventData, event_name: platformNames.facebook };
      const response = await fetch(`${CAPI_ENDPOINT}/facebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fbPayload),
      });
      if (!response.ok) {
        console.warn('Facebook CAPI tracking failed:', response.status);
      }
    } catch (error) {
      console.warn('Facebook CAPI tracking error:', error);
    }
  }

  if (rdt_cid) {
    try {
      const rdtPayload = { ...eventData, event_name: platformNames.reddit };
      const response = await fetch(`${CAPI_ENDPOINT}/reddit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rdtPayload),
      });
      if (!response.ok) {
        console.warn('Reddit CAPI tracking failed:', response.status);
      }
    } catch (error) {
      console.warn('Reddit CAPI tracking error:', error);
    }
  }
};

const makeEventId = (prefix: string): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

/**
 * Track a signup/registration conversion
 * Sends CompleteRegistration event to Meta and SignUp to Reddit.
 * Pass the new account's email when available — the capi-worker hashes it,
 * and it strengthens matching when the click id has gone stale.
 */
export const trackSignupConversion = (email?: string): void => {
  // First-party funnel event — ungated like Rybbit pageviews (NOT behind the
  // ad-consent cookie), so every signup is a chartable Rybbit event even when
  // ad measurement was declined or the visitor never saw the banner. Campaign
  // UTMs ride in automatically (emitRybbitEvent merges them into every event);
  // the marketing site synthesizes utm_source=google for gclid-only Google
  // Ads clicks.
  emitRybbitEvent('signup');

  if (!hasAdConsent()) return;

  // Google Ads signup conversion (client-side gtag; attribution via the
  // _gcl_* cookies / carried gclid — no click id needed from qp_attr)
  fireGoogleSignupConversion();

  const attr = getAttribution();

  // funnel join in our own metrics, keyed by the marketing-site visitor id
  if (attr.v) {
    void track(attr.v, 'signup_attributed', {
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
    });
  }

  if (!attr.fbclid && !attr.rdt_cid) return;

  // Fire and forget
  void sendConversionEvent(
    {
      event_name: 'CompleteRegistration',
      user_data: { ...getMatchKeys(attr), email },
      event_id: makeEventId('signup'),
    },
    { facebook: 'CompleteRegistration', reddit: 'SignUp' },
  );
};

/**
 * Initialize click ID capture on page load, and report the author-visit
 * funnel stage (PageVisit to the ad platforms, author_visit to our metrics)
 * once per browser session. Call this early in the app lifecycle.
 */
export const initConversionTracking = (): void => {
  if (!hasAdConsent()) return;
  const attr = getAttribution();

  try {
    if (sessionStorage.getItem('qp_author_visit_sent')) return;
    sessionStorage.setItem('qp_author_visit_sent', '1');
  } catch {
    return;
  }

  if (attr.v) {
    void track(attr.v, 'author_visit', {
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
      landing_page: attr.lp,
    });
  }

  if (attr.fbclid || attr.rdt_cid) {
    void sendConversionEvent(
      {
        event_name: 'PageView',
        user_data: getMatchKeys(attr),
        event_id: makeEventId('visit'),
      },
      { facebook: 'PageView', reddit: 'PageView' },
    );
  }
};
